import {
  useAppStatus,
  useCancelSync,
  useStartSync,
  useSyncPlan,
  useSyncRun,
  type SyncScope,
} from "@web/features/library/api.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { formatDateTime } from "@web/lib/format.ts";
import { coyLog } from "@web/lib/coyLog.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { cn } from "@web/lib/utils.ts";

function formatCountdown(sec: number): string {
  if (sec <= 0) return "ready now";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `~${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `~${m}m ${s}s` : `~${m}m`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-line bg-panel px-3 py-3">
      <div className="text-xs tracking-wider text-faint uppercase">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums tracking-tight">{value}</div>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

type Job = NonNullable<ReturnType<typeof useSyncPlan>["data"]>["jobs"][number];
type Health = NonNullable<ReturnType<typeof useSyncPlan>["data"]>["health"];

function SyncProgressCard({
  progress,
  running,
  onCancel,
  cancelling,
}: {
  progress: {
    scope: string;
    label: string;
    detail: string;
    percent: number;
    api_calls: number;
    budget: number;
  };
  running: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-accent/40 bg-panel-elevated px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            Sync progress · {progress.scope}
          </p>
          <p className="mt-1 text-sm font-medium text-text">{progress.label}</p>
          <p className="mt-0.5 text-xs text-muted">{progress.detail}</p>
        </div>
        {running && (
          <Button variant="secondary" size="sm" disabled={cancelling} onClick={onCancel}>
            {cancelling ? "Cancelling…" : "Cancel"}
          </Button>
        )}
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-panel"
        role="progressbar"
        aria-valuenow={progress.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Job progress"
      >
        <div
          className={cn(
            "h-full rounded-full bg-accent transition-[width] duration-500 ease-out",
            running && "animate-pulse",
          )}
          style={{ width: `${Math.max(2, progress.percent)}%` }}
        />
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-xs text-muted">
        <span className="tabular-nums">Job {progress.percent}% complete</span>
        <span className="tabular-nums">
          API used {progress.api_calls.toLocaleString()} / this-run cap{" "}
          {progress.budget.toLocaleString()}
        </span>
      </div>
      <p className="text-xs text-faint">
        Cap is frozen when the run starts. New runs use current budgets (liked 250 ·
        full 600).
      </p>
    </section>
  );
}

function JobCard({
  job,
  primary,
  disabled,
  starting,
  onRun,
}: {
  job: Job;
  primary?: boolean;
  disabled: boolean;
  starting: boolean;
  onRun: () => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border px-3 py-3",
        primary ? "border-accent/50 bg-panel-elevated" : "border-line bg-panel",
      )}
    >
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-text">{job.title}</p>
        <p className="text-xs text-muted">{job.work}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-faint">Cost</dt>
          <dd className="tabular-nums text-text">
            {job.cost_calls.toLocaleString()} calls
            <span className="text-muted"> · {job.items_per_call}/page</span>
          </dd>
          <dt className="text-faint">Time</dt>
          <dd className="tabular-nums text-text">{formatDuration(job.time_sec)} at ~2s/page</dd>
          <dt className="text-faint">Result</dt>
          <dd className="text-muted">{job.result}</dd>
        </dl>
      </div>
      <Button size={primary ? "default" : "sm"} disabled={disabled} onClick={onRun}>
        {starting ? "Starting…" : primary ? `Full sync · ${job.cost_calls} calls` : `Run · ${job.cost_calls} calls`}
      </Button>
    </div>
  );
}

/**
 * Sync readiness, Full sync primary, real Spotify 429 banner, mirror health.
 */
export function LibraryStatusPanel() {
  const qc = useQueryClient();
  const status = useAppStatus({ refetchInterval: 5_000 });
  const syncQuery = useSyncRun({ refetchInterval: 1_000 });
  const planQuery = useSyncPlan({ refetchInterval: 10_000 });
  const startSync = useStartSync();
  const cancelSync = useCancelSync();
  const lastSyncLog = useRef<string | null>(null);
  const last429Log = useRef<string | null>(null);

  const run = syncQuery.data?.run ?? null;
  const progress = syncQuery.data?.progress ?? null;
  const plan = planQuery.data ?? null;
  const running = run?.status === "running";
  const health: Health | null = plan?.health ?? null;

  useEffect(() => {
    if (!run) return;
    const key = `${run.id}|${run.status}|${run.phase}|${run.api_calls}|${progress?.percent ?? ""}|${run.error ?? ""}`;
    if (key === lastSyncLog.current) return;
    const wasRunning = lastSyncLog.current?.includes("|running|") ?? false;
    lastSyncLog.current = key;
    coyLog.info(`sync #${run.id}`, {
      status: run.status,
      phase: run.phase,
      api_calls: run.api_calls,
      progress,
      counts: run.counts,
      error: run.error,
    });
    if (run.status === "quota_paused") {
      coyLog.error("Sync stopped on REAL Spotify 429 — see Settings banner + server [spotify-429] logs", {
        error: run.error,
      });
    }
    if (wasRunning && run.status !== "running") {
      void qc.invalidateQueries({ queryKey: ["status"] });
      void qc.invalidateQueries({ queryKey: ["sync-plan"] });
      void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
      void qc.invalidateQueries({ queryKey: ["artists"] });
      void qc.invalidateQueries({ queryKey: ["playlists"] });
      void qc.invalidateQueries({ queryKey: ["graph"] });
    }
  }, [run, progress, qc]);

  useEffect(() => {
    const hit = health?.last_429;
    if (!hit) return;
    const key = `${hit.at}|${hit.path}|${hit.recovered}`;
    if (key === last429Log.current) return;
    last429Log.current = key;
    if (hit.recovered) {
      coyLog.warn("Spotify soft 429 — backed off and continued", hit);
    } else {
      coyLog.error("Spotify HARD 429 — cooldown / sync stop", hit);
    }
  }, [health?.last_429]);

  if (status.isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  if (status.isError || !status.data) {
    return (
      <EmptyState
        title="Couldn't load library status"
        description={status.error instanceof Error ? status.error.message : "Unknown error"}
        action={
          <Button variant="secondary" size="sm" onClick={() => void status.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const s = status.data;
  const { counts, quota, sync } = s;
  const coverPct =
    counts.tracks === 0 ? 0 : Math.round((counts.tracks_with_cover / counts.tracks) * 100);

  const locked = health?.state === "locked_out" || quota.active;
  const canStart =
    s.connected && !locked && !running && !startSync.isPending && !cancelSync.isPending;

  const fullJob = plan?.jobs.find((j) => j.scope === "full") ?? null;
  const partialJobs = (plan?.jobs ?? []).filter((j) => j.scope !== "full");
  const last429 = health?.last_429 ?? s.last_429;

  function runScope(scope: SyncScope) {
    if (locked) {
      coyLog.warn("Sync blocked — Spotify cooldown active", health);
      return;
    }
    startSync.mutate({ scope });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Library</h2>
        <p className="mt-1 text-sm text-muted">
          Normal path: one <span className="text-text">Full sync</span> (~2s between pages). It
          fills playlists first, then liked + covers. Partial jobs are for recovery only.
        </p>
      </div>

      {health && (
        <section
          className={cn(
            "space-y-2 rounded-lg border px-4 py-3",
            health.state === "healthy" && "border-line bg-panel",
            health.state === "throttled" && "border-status-pending/50 bg-status-pending/10",
            health.state === "locked_out" && "border-status-cancelled/50 bg-status-cancelled/10",
          )}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-text">{health.label}</p>
            <p className="text-xs tabular-nums text-muted">
              today {health.calls_today.toLocaleString()} web · last 30s {health.calls_last_30s}
              {health.remaining_sec > 0 ? ` · wait ${formatCountdown(health.remaining_sec)}` : ""}
            </p>
          </div>
          <p className="text-xs text-muted">{health.detail}</p>
          <p className="text-xs text-faint">{health.caveat}</p>
        </section>
      )}

      {last429 && (
        <section
          className={cn(
            "space-y-2 rounded-lg border px-4 py-3 text-sm",
            last429.recovered
              ? "border-status-pending/50 bg-status-pending/10"
              : "border-status-cancelled/50 bg-status-cancelled/10",
          )}
        >
          <p
            className={cn(
              "font-medium",
              last429.recovered ? "text-status-pending" : "text-status-cancelled",
            )}
          >
            {last429.recovered ? "Spotify soft 429 (recovered)" : "Spotify HARD 429"}
            {" · "}
            {last429.reason}
          </p>
          <p className="text-xs text-muted">
            {formatDateTime(last429.at)} · <span className="font-mono">{last429.path}</span>
            {last429.label ? ` · ${last429.label}` : ""} · Retry-After {last429.retry_after_sec}s
            {last429.recovered ? " · we backed off and continued" : " · sync/cooldown stopped"}
          </p>
          {last429.body_snippet && (
            <p className="font-mono text-xs break-all text-faint">{last429.body_snippet}</p>
          )}
        </section>
      )}

      {progress && (running || run) && (
        <SyncProgressCard
          progress={progress}
          running={running}
          cancelling={cancelSync.isPending}
          onCancel={() => cancelSync.mutate()}
        />
      )}

      <section className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
          Full sync (recommended)
        </h3>
        {fullJob ? (
          <JobCard
            job={fullJob}
            primary
            disabled={!canStart}
            starting={startSync.isPending && startSync.variables?.scope === "full"}
            onRun={() => runScope("full")}
          />
        ) : planQuery.isLoading ? (
          <Skeleton className="h-36 rounded-lg" />
        ) : null}
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">
          Partial jobs (optional)
        </h3>
        <p className="text-xs text-muted">
          Use only to finish a piece. Playlists also redoes liked. Artist photos
          are not fetched from Spotify.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {partialJobs.map((job) => (
            <JobCard
              key={job.scope}
              job={job}
              disabled={!canStart}
              starting={startSync.isPending && startSync.variables?.scope === job.scope}
              onRun={() => runScope(job.scope)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">Connection</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Stat
            label="Spotify"
            value={s.connected ? (s.api_ok ? "Connected" : "Connected · API error") : "Not connected"}
            hint={
              s.user
                ? `${s.user.display_name ?? s.user.id}${s.user.email ? ` · ${s.user.email}` : ""}`
                : (s.error ?? undefined)
            }
          />
          <Stat
            label="Quota"
            value={quota.active ? `Paused · ${formatCountdown(quota.remaining_sec)}` : "Clear"}
            hint={
              quota.active
                ? `Ready at ${quota.ready_at ? formatDateTime(quota.ready_at) : "—"} (${quota.reason ?? "rate limit"})`
                : `Today ${s.api_today.web_api_calls} web API calls logged`
            }
          />
        </div>
        <p className="text-xs text-muted">
          Mirror source: <span className="text-text">{s.mirror.source}</span> — {s.mirror.note}
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">Mirror</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Tracks" value={counts.tracks.toLocaleString()} />
          <Stat label="Liked" value={counts.liked.toLocaleString()} />
          <Stat label="Albums" value={counts.albums.toLocaleString()} />
          <Stat label="Artists" value={counts.artists.toLocaleString()} />
          <Stat label="Playlists" value={counts.playlists.toLocaleString()} />
          <Stat
            label="Last sync"
            value={run ? run.status : sync ? sync.status : "None yet"}
            hint={
              run
                ? `${run.cursor?.scope ?? "—"} · ${run.phase ?? "—"} · ${run.api_calls} calls` +
                  (run.error ? ` · ${run.error}` : "")
                : sync
                  ? `${sync.phase} · ${formatDateTime(sync.finished_at ?? sync.started_at)} · ${sync.api_calls} calls`
                  : "Run Full sync"
            }
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold tracking-wider text-faint uppercase">Covers</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Stat
            label="Tracks with cover"
            value={`${counts.tracks_with_cover.toLocaleString()} · ${coverPct}%`}
            hint={`${counts.albums_with_cover.toLocaleString()} albums have a cover URL`}
          />
          <Stat
            label="Awaiting remap"
            value={counts.tracks_unmapped.toLocaleString()}
            hint="map_version = 0 — Full/Playlists remaps playlist-only tracks"
          />
        </div>
      </section>

      {(run?.error || sync?.error) && run?.status !== "running" && (
        <section className="rounded-lg border border-status-cancelled/40 bg-status-cancelled/10 px-4 py-3 text-sm">
          <p className="font-medium text-status-cancelled">Last sync note</p>
          <p className="mt-1 text-muted">{run?.error ?? sync?.error}</p>
        </section>
      )}
    </div>
  );
}
